// F14b (FR-RD10): the desktop app checks this repo's release `latest.json` once per launch
// (clients/desktop/src/updater.rs) and offers "Install and restart" when a newer signed build
// exists. Silent on any error (no endpoint yet, unsigned dev build, offline): an updater that
// can't reach its channel must not nag. Desktop shell only; the browser and mobile never see it.
import { useEffect, useState } from "react";
import { Button, Callout } from "@grackle/ui";
import { checkForUpdate, installUpdate, isDesktop, isMobileShell, type UpdateInfo } from "../desktop";
import "./DesktopUpdateNotice.css";

export default function DesktopUpdateNotice() {
  const [update, setUpdate] = useState<UpdateInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!isDesktop() || isMobileShell()) return;
    let live = true;
    void checkForUpdate()
      .then((info) => {
        if (live) setUpdate(info);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  if (!update || dismissed) return null;

  async function install() {
    setBusy(true);
    setError(null);
    try {
      await installUpdate();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  return (
    <div className="update-notice" data-testid="desktop-update-notice">
      <Callout tone="info" icon="cloud-arrow-up">
        Grackle {update.version} is available (you have {update.current_version}).
        {error ? <span className="update-notice__error"> {error}</span> : null}
        <span className="update-notice__actions">
          <Button size="sm" onClick={() => void install()} disabled={busy}>
            {busy ? "Installing…" : "Install and restart"}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setDismissed(true)} disabled={busy}>
            Later
          </Button>
        </span>
      </Callout>
    </div>
  );
}
